import Base from '../base/base';
import { buildRequestUrl } from '../../shared/utils/request-url';

type CmsVisibilityTarget = string | string[];

interface CmsSectionVisibilityOptions {
  middlewareBaseUrl?: string;
  collectionId?: string;
  sectionMap?: Record<string, CmsVisibilityTarget>;
  debug?: boolean;
  [key: string]: any;
}

export default class CmsSectionVisibility extends Base {
  private middlewareBaseUrl: string;
  private collectionId: string;
  private sectionMap: Record<string, CmsVisibilityTarget>;

  constructor(options: CmsSectionVisibilityOptions = {}) {
    super(options.debug || false);

    this.middlewareBaseUrl = options.middlewareBaseUrl || 'https://wf.mw.brillnt.com';
    this.collectionId = options.collectionId || '';
    this.sectionMap = options.sectionMap || {
      'replace-with-cms-field-key': []
    };

    this.init();
  }

  init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.applyCmsVisibility, { once: true });
      return;
    }

    this.applyCmsVisibility();
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return false;
  }

  private normalizeTargets(target: CmsVisibilityTarget): string[] {
    if (Array.isArray(target)) {
      return target.filter(Boolean);
    }

    return target ? [target] : [];
  }

  private hideBySelector(selector: string): void {
    if (!selector) return;

    const elements = Array.from(document.querySelectorAll<HTMLElement>(selector));
    elements.forEach((element) => {
      element.style.display = 'none';
    });
  }

  private applyCmsVisibility = async (): Promise<void> => {
    try {
      if (!this.collectionId) {
        this.log('CmsSectionVisibility skipped: collectionId is missing');
        return;
      }

      const requestUrl = buildRequestUrl(`${this.middlewareBaseUrl}/api/collections/${this.collectionId}`);
      const response = await fetch(requestUrl);
      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const firstItem = Array.isArray(payload.items) ? payload.items[0] : null;
      const fieldData = firstItem && firstItem.fieldData ? firstItem.fieldData : {};

      Object.entries(this.sectionMap).forEach(([cmsKey, target]) => {
        if (this.toBoolean(fieldData[cmsKey])) {
          this.normalizeTargets(target).forEach((selector) => {
            this.hideBySelector(selector);
          });
        }
      });
    } catch (error) {
      this.log('CmsSectionVisibility request failed', error);
    }
  };
}
